/*
 * PDF side of /djvu-to-pdf: a streaming PDF writer, a CCITT group 4 encoder
 * for 1-bit masks, and the invisible text layer.
 *
 * Streaming: every object is written as soon as it exists; only the offsets
 * table stays in memory, so a thousand-page book never sits in RAM as one
 * PDF. The page tree, outline and catalogue are written last.
 *
 * Text layer: one "glyph-less" TrueType font whose every CID maps to an empty
 * glyph, Identity-H encoding and an identity ToUnicode map — the approach of
 * Tesseract and OCRmyPDF. Any script is searchable and copyable, and the
 * file carries no real font at all.
 *
 * Environment-neutral (worker, page, Node). ES2019 syntax.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.DjVuPdf = api;
})(typeof self !== 'undefined' ? self : (typeof globalThis !== 'undefined' ? globalThis : this), function () {
  'use strict';

  // ------------------------------------------------------------------
  // Byte helpers.
  // ------------------------------------------------------------------
  function latin1(s) {
    var b = new Uint8Array(s.length);
    for (var i = 0; i < s.length; i++) b[i] = s.charCodeAt(i) & 0xff;
    return b;
  }
  function num(v) {
    if (Number.isInteger(v)) return String(v);
    var s = v.toFixed(4);
    s = s.replace(/0+$/, '').replace(/\.$/, '');
    return s === '-0' ? '0' : s;
  }
  function hex2(n) { return (n < 16 ? '0' : '') + n.toString(16).toUpperCase(); }
  // Text string for Info / outline titles: PDFDocEncoding for plain ASCII,
  // UTF-16BE with BOM otherwise (Cyrillic outline titles need it).
  function pdfText(s) {
    s = String(s == null ? '' : s);
    var ascii = true;
    for (var i = 0; i < s.length; i++) {
      var c = s.charCodeAt(i);
      if (c < 32 || c > 126) { ascii = false; break; }
    }
    if (ascii) return '(' + s.replace(/[\\()]/g, '\\$&') + ')';
    var out = '<FEFF';
    for (i = 0; i < s.length; i++) {
      var u = s.charCodeAt(i);
      out += hex2(u >> 8) + hex2(u & 0xff);
    }
    return out + '>';
  }
  // A URI is ASCII: percent-encode everything else as UTF-8.
  function pdfUri(u) {
    var enc = '';
    try { enc = encodeURI(decodeURI(String(u))); } catch (e) { enc = encodeURI(String(u)); }
    return '(' + enc.replace(/[\\()]/g, '\\$&') + ')';
  }
  function pdfDate(d) {
    function p(n) { return (n < 10 ? '0' : '') + n; }
    return '(D:' + d.getUTCFullYear() + p(d.getUTCMonth() + 1) + p(d.getUTCDate()) +
      p(d.getUTCHours()) + p(d.getUTCMinutes()) + p(d.getUTCSeconds()) + 'Z)';
  }

  // ------------------------------------------------------------------
  // CCITT group 4 (T.6) encoder — packed rows, 1 = foreground (black).
  // Decoded with the default BlackIs1 = false, foreground samples come out
  // as 0, which is exactly what an ImageMask paints.
  // ------------------------------------------------------------------
  var WT = ['00110101', '000111', '0111', '1000', '1011', '1100', '1110', '1111', '10011', '10100',
    '00111', '01000', '001000', '000011', '110100', '110101', '101010', '101011', '0100111', '0001100',
    '0001000', '0010111', '0000011', '0000100', '0101000', '0101011', '0010011', '0100100', '0011000', '00000010',
    '00000011', '00011010', '00011011', '00010010', '00010011', '00010100', '00010101', '00010110', '00010111', '00101000',
    '00101001', '00101010', '00101011', '00101100', '00101101', '00000100', '00000101', '00001010', '00001011', '01010010',
    '01010011', '01010100', '01010101', '00100100', '00100101', '01011000', '01011001', '01011010', '01011011', '01001010',
    '01001011', '00110010', '00110011', '00110100'];
  var WM = ['11011', '10010', '010111', '0110111', '00110110', '00110111', '01100100', '01100101', '01101000', '01100111',
    '011001100', '011001101', '011010010', '011010011', '011010100', '011010101', '011010110', '011010111', '011011000', '011011001',
    '011011010', '011011011', '010011000', '010011001', '010011010', '011000', '010011011'];
  var BT = ['0000110111', '010', '11', '10', '011', '0011', '0010', '00011', '000101', '000100',
    '0000100', '0000101', '0000111', '00000100', '00000111', '000011000', '0000010111', '0000011000', '0000001000', '00001100111',
    '00001101000', '00001101100', '00000110111', '00000101000', '00000010111', '00000011000', '000011001010', '000011001011', '000011001100', '000011001101',
    '000001101000', '000001101001', '000001101010', '000001101011', '000011010010', '000011010011', '000011010100', '000011010101', '000011010110', '000011010111',
    '000001101100', '000001101101', '000011011010', '000011011011', '000001010100', '000001010101', '000001010110', '000001010111', '000001100100', '000001100101',
    '000001010010', '000001010011', '000000100100', '000000110111', '000000111000', '000000100111', '000000101000', '000001011000', '000001011001', '000000101011',
    '000000101100', '000001011010', '000001100110', '000001100111'];
  var BM = ['0000001111', '000011001000', '000011001001', '000001011011', '000000110011', '000000110100', '000000110101', '0000001101100', '0000001101101', '0000001001010',
    '0000001001011', '0000001001100', '0000001001101', '0000001110010', '0000001110011', '0000001110100', '0000001110101', '0000001110110', '0000001110111', '0000001010010',
    '0000001010011', '0000001010100', '0000001010101', '0000001011010', '0000001011011', '0000001100100', '0000001100101'];
  var XM = ['00000001000', '00000001100', '00000001101', '000000010010', '000000010011', '000000010100', '000000010101',
    '000000010110', '000000010111', '000000011100', '000000011101', '000000011110', '000000011111'];

  function codeOf(s) { return { len: s.length, bits: parseInt(s, 2) }; }
  var CODES = null;
  function codes() {
    if (CODES) return CODES;
    var i;
    CODES = { wt: [], wm: [], bt: [], bm: [], xm: [] };
    for (i = 0; i < WT.length; i++) CODES.wt.push(codeOf(WT[i]));
    for (i = 0; i < WM.length; i++) CODES.wm.push(codeOf(WM[i]));
    for (i = 0; i < BT.length; i++) CODES.bt.push(codeOf(BT[i]));
    for (i = 0; i < BM.length; i++) CODES.bm.push(codeOf(BM[i]));
    for (i = 0; i < XM.length; i++) CODES.xm.push(codeOf(XM[i]));
    return CODES;
  }

  function BitWriter(capacity) {
    this.buf = new Uint8Array(Math.max(1024, capacity | 0));
    this.len = 0;
    this.acc = 0;
    this.nacc = 0;
  }
  BitWriter.prototype.put = function (bits, n) {
    // n <= 24
    this.acc = (this.acc << n) | bits;
    this.nacc += n;
    while (this.nacc >= 8) {
      this.nacc -= 8;
      if (this.len >= this.buf.length) this.grow();
      this.buf[this.len++] = (this.acc >> this.nacc) & 0xff;
    }
    this.acc &= (1 << this.nacc) - 1;
  };
  BitWriter.prototype.grow = function () {
    var b = new Uint8Array(this.buf.length * 2);
    b.set(this.buf);
    this.buf = b;
  };
  BitWriter.prototype.finish = function () {
    if (this.nacc > 0) this.put(0, 8 - this.nacc);
    return this.buf.slice(0, this.len);
  };

  function putRun(bw, run, black) {
    var c = codes();
    var term = black ? c.bt : c.wt, makeup = black ? c.bm : c.wm;
    while (run >= 2560) { bw.put(c.xm[12].bits, c.xm[12].len); run -= 2560; }
    if (run >= 64) {
      var m = run >> 6; // multiple of 64, 1..39
      var code = m <= 27 ? makeup[m - 1] : c.xm[m - 28];
      bw.put(code.bits, code.len);
      run -= m << 6;
    }
    bw.put(term[run].bits, term[run].len);
  }

  // Changing elements of one packed row: positions where the colour differs
  // from the pixel before (pixel -1 is white), followed by width, width.
  // A whole byte of the current colour holds no change and is skipped — a
  // text page is mostly white bytes, a per-colour stencil almost entirely.
  function changes(bits, off, w, out) {
    var n = 0, prev = 0;
    var full = w >> 3;
    for (var bi = 0; bi < full; bi++) {
      var byte = bits[off + bi];
      if (byte === (prev ? 0xff : 0)) continue;
      var x = bi << 3;
      for (var k = 7; k >= 0; k--, x++) {
        var v = (byte >> k) & 1;
        if (v !== prev) { out[n++] = x; prev = v; }
      }
    }
    for (var x2 = full << 3; x2 < w; x2++) {
      var v2 = (bits[off + (x2 >> 3)] >> (7 - (x2 & 7))) & 1;
      if (v2 !== prev) { out[n++] = x2; prev = v2; }
    }
    out[n] = w; out[n + 1] = w;
    return n;
  }

  // `win` (optional) places the w x h bitmap at (x, y) — y counted from the
  // top — inside a larger white canvas of win.width x win.height, which is
  // what gets encoded; the canvas itself is never allocated.
  function encodeG4(bits, w, h, stride, win) {
    stride = stride || ((w + 7) >> 3);
    var W = win ? win.width : w, H = win ? win.height : h;
    var dx = win ? win.x : 0, dy = win ? win.y : 0;
    var bw = new BitWriter(Math.max(stride * h / 8, H / 4));
    var ref = new Int32Array(W + 4), cur = new Int32Array(W + 4);
    var refN = 0;
    ref[0] = W; ref[1] = W;
    for (var y = 0; y < H; y++) {
      var curN;
      var sy = y - dy;
      if (sy < 0 || sy >= h) {
        curN = 0;
        cur[0] = W; cur[1] = W;
      } else if (!win) {
        curN = changes(bits, sy * stride, w, cur);
      } else {
        curN = changes(bits, sy * stride, w, cur);
        // shift into the canvas; a row that ends inside the bitmap on black
        // turns white again at its right edge
        for (var t = 0; t < curN; t++) cur[t] += dx;
        if ((curN & 1) && dx + w < W) cur[curN++] = dx + w;
        cur[curN] = W; cur[curN + 1] = W;
      }
      var a0 = -1, color = 0, ai = 0, bi = 0;
      while (a0 < W) {
        // a1: first change on the coding line right of a0
        while (ai < curN && cur[ai] <= a0) ai++;
        var a1 = ai < curN ? cur[ai] : W;
        var a2 = ai + 1 < curN ? cur[ai + 1] : W;
        // b1: first change on the reference line right of a0, of the colour
        // opposite to a0's (even index = to black), then b2
        bi = bi > 1 ? bi - 2 : 0;
        while (bi < refN && ref[bi] <= a0) bi++;
        if ((bi & 1) !== color) bi++;
        var b1 = bi < refN ? ref[bi] : W;
        var b2 = bi + 1 < refN ? ref[bi + 1] : W;
        if (b2 < a1) {                       // pass
          bw.put(1, 4);
          a0 = b2;
        } else {
          var d = a1 - b1;
          if (d >= -3 && d <= 3) {           // vertical
            switch (d) {
              case 0: bw.put(1, 1); break;
              case 1: bw.put(3, 3); break;
              case -1: bw.put(2, 3); break;
              case 2: bw.put(3, 6); break;
              case -2: bw.put(2, 6); break;
              case 3: bw.put(3, 7); break;
              default: bw.put(2, 7); break;
            }
            a0 = a1;
            color ^= 1;
          } else {                           // horizontal
            bw.put(1, 3);
            var start = a0 < 0 ? 0 : a0;
            putRun(bw, a1 - start, color === 1);
            putRun(bw, a2 - a1, color === 0);
            a0 = a2;
          }
        }
      }
      var tmp = ref; ref = cur; cur = tmp;
      refN = curN;
    }
    // EOFB: two EOL codes
    bw.put(1, 12);
    bw.put(1, 12);
    return bw.finish();
  }

  // ------------------------------------------------------------------
  // Glyph-less TrueType font: .notdef + one empty glyph, 1000 units/em,
  // every advance 500. Built once, embedded once per PDF.
  // ------------------------------------------------------------------
  var GLYPHLESS_ADVANCE = 500;
  function buildGlyphlessFont() {
    function u16(a, v) { a.push((v >> 8) & 255, v & 255); }
    function i16(a, v) { u16(a, v & 0xffff); }
    function u32(a, v) { a.push((v >>> 24) & 255, (v >>> 16) & 255, (v >>> 8) & 255, v & 255); }
    var tables = {};
    var head = [];
    u32(head, 0x00010000); u32(head, 0x00010000); u32(head, 0); u32(head, 0x5F0F3CF5);
    u16(head, 0x000B); u16(head, 1000);               // flags, unitsPerEm
    u32(head, 0); u32(head, 0); u32(head, 0); u32(head, 0); // created, modified
    i16(head, 0); i16(head, 0); i16(head, 1000); i16(head, 1000); // bbox
    u16(head, 0); u16(head, 3); i16(head, 2); i16(head, 0); i16(head, 0); // macStyle, lowestRec, dir, locFmt(short), glyphFmt
    tables.head = head;
    var hhea = [];
    u32(hhea, 0x00010000); i16(hhea, 1000); i16(hhea, 0); i16(hhea, 0); u16(hhea, GLYPHLESS_ADVANCE);
    i16(hhea, 0); i16(hhea, 0); i16(hhea, 1000); i16(hhea, 1); i16(hhea, 0); i16(hhea, 0);
    for (var r = 0; r < 4; r++) i16(hhea, 0);
    i16(hhea, 0); u16(hhea, 2);
    tables.hhea = hhea;
    var maxp = [];
    u32(maxp, 0x00010000); u16(maxp, 2);
    for (r = 0; r < 13; r++) u16(maxp, r === 0 ? 0 : (r === 1 ? 0 : (r === 4 ? 1 : 0)));
    tables.maxp = maxp;
    var hmtx = [];
    u16(hmtx, GLYPHLESS_ADVANCE); i16(hmtx, 0); u16(hmtx, GLYPHLESS_ADVANCE); i16(hmtx, 0);
    tables.hmtx = hmtx;
    tables.loca = [0, 0, 0, 0, 0, 0]; // short offsets: both glyphs empty
    tables.glyf = [0, 0]; // one padding word (empty glyphs have no data)
    var cmap = [];                     // format 4: U+0020 -> glyph 1 (for tools that want a cmap)
    u16(cmap, 0); u16(cmap, 1); u16(cmap, 3); u16(cmap, 1); u32(cmap, 12);
    u16(cmap, 4); u16(cmap, 32); u16(cmap, 0);
    u16(cmap, 4); u16(cmap, 4); u16(cmap, 1); u16(cmap, 0); // segCountX2=4, searchRange, entrySelector, rangeShift
    u16(cmap, 0x20); u16(cmap, 0xFFFF);  // endCode
    u16(cmap, 0);                        // reserved
    u16(cmap, 0x20); u16(cmap, 0xFFFF);  // startCode
    i16(cmap, 1 - 0x20); i16(cmap, 1);   // idDelta
    u16(cmap, 0); u16(cmap, 0);          // idRangeOffset
    tables.cmap = cmap;
    var name = [];
    var nm = 'GlyphLessFont';
    u16(name, 0); u16(name, 1); u16(name, 6);
    u16(name, 3); u16(name, 1); u16(name, 0x409); u16(name, 6); u16(name, nm.length * 2); u16(name, 0);
    for (var k = 0; k < nm.length; k++) u16(name, nm.charCodeAt(k));
    tables.name = name;
    var post = [];
    u32(post, 0x00030000); u32(post, 0); i16(post, 0); i16(post, 0); u32(post, 0); u32(post, 0); u32(post, 0); u32(post, 0); u32(post, 0);
    tables.post = post;
    var os2 = [];
    u16(os2, 3); i16(os2, GLYPHLESS_ADVANCE); u16(os2, 400); u16(os2, 5); u16(os2, 0);
    for (r = 0; r < 11; r++) i16(os2, 0);
    for (r = 0; r < 10; r++) os2.push(0); // panose
    u32(os2, 0); u32(os2, 0); u32(os2, 0); u32(os2, 0);
    os2.push(0x54, 0x49, 0x4D, 0x42); // vendor
    u16(os2, 0x40); u16(os2, 0x20); u16(os2, 0xFFFF);
    i16(os2, 1000); i16(os2, 0); i16(os2, 0); u16(os2, 1000); u16(os2, 0);
    u32(os2, 1); u32(os2, 0); i16(os2, 0); i16(os2, 1000); u16(os2, 0); u16(os2, 0x20); u16(os2, 0);
    tables['OS/2'] = os2;
    var tags = Object.keys(tables).sort();
    var numTables = tags.length;
    var out = [];
    u32(out, 0x00010000); u16(out, numTables);
    var es = Math.floor(Math.log(numTables) / Math.LN2);
    u16(out, 16 << es); u16(out, es); u16(out, numTables * 16 - (16 << es));
    var offset = 12 + 16 * numTables;
    var bodies = [];
    for (var t = 0; t < tags.length; t++) {
      var data = tables[tags[t]];
      var padded = data.slice();
      while (padded.length % 4) padded.push(0);
      var sum = 0;
      for (var q = 0; q < padded.length; q += 4) sum = (sum + ((padded[q] << 24) >>> 0) + (padded[q + 1] << 16) + (padded[q + 2] << 8) + padded[q + 3]) >>> 0;
      for (var c2 = 0; c2 < 4; c2++) out.push(tags[t].charCodeAt(c2));
      u32(out, sum); u32(out, offset); u32(out, data.length);
      bodies.push(padded);
      offset += padded.length;
    }
    for (t = 0; t < bodies.length; t++) for (q = 0; q < bodies[t].length; q++) out.push(bodies[t][q]);
    return new Uint8Array(out);
  }

  // ------------------------------------------------------------------
  // Streaming writer.
  // sink(Uint8Array) receives the bytes in order; deflate(Uint8Array) ->
  // Uint8Array (zlib) is used for text streams, may be null (stored raw).
  // ------------------------------------------------------------------
  function PdfWriter(opts) {
    this.sink = opts.sink;
    this.deflate = opts.deflate || null;
    this.offset = 0;
    this.offsets = [0];
    this.pages = [];
    this.nextId = 1;
    this.pagesId = this.alloc();
    this.fontId = 0;
    this.write(latin1('%PDF-1.5\n%\xE2\xE3\xCF\xD3\n'));
  }
  PdfWriter.prototype.alloc = function () { var id = this.nextId++; this.offsets[id] = -1; return id; };
  // Page object ids are reserved up front so that links and outline entries
  // can point at pages that are not written yet.
  PdfWriter.prototype.reservePages = function (n) {
    this.pages = [];
    for (var i = 0; i < n; i++) this.pages.push(this.alloc());
  };
  PdfWriter.prototype.write = function (bytes) { this.sink(bytes); this.offset += bytes.length; };
  PdfWriter.prototype.object = function (id, dict, stream) {
    this.offsets[id] = this.offset;
    if (stream) {
      this.write(latin1(id + ' 0 obj\n' + dict.replace(/>>\s*$/, '/Length ' + stream.length + '>>') + '\nstream\n'));
      this.write(stream);
      this.write(latin1('\nendstream\nendobj\n'));
    } else {
      this.write(latin1(id + ' 0 obj\n' + dict + '\nendobj\n'));
    }
    return id;
  };
  PdfWriter.prototype.stream = function (dict, bytes, compress) {
    var id = this.alloc();
    if (compress && this.deflate) {
      var z = this.deflate(bytes);
      if (z && z.length < bytes.length) return this.object(id, dict.replace(/>>\s*$/, '/Filter/FlateDecode>>'), z);
    }
    return this.object(id, dict, bytes);
  };
  // A stream that may already be compressed elsewhere: {bytes, z: true|false}.
  // The page pipeline deflates in its worker (CompressionStream is async), so
  // the writer on the page never compresses anything itself.
  PdfWriter.prototype.put = function (dict, s) {
    if (s.z) return this.object(this.alloc(), dict.replace(/>>\s*$/, '/Filter/FlateDecode>>'), s.bytes);
    return this.stream(dict, s.bytes, true);
  };
  // The invisible-text font, written on first use. `fontStreams` (from
  // DjVuPdf.fontStreams) carries its three streams compressed in advance.
  PdfWriter.prototype.textFont = function () {
    if (this.fontId) return this.fontId;
    var fs = this.fontStreams || fontStreamsRaw();
    var fileId = this.put('<</Length1 ' + fs.ttfLength + '>>', fs.ttf);
    var mapId = this.put('<<>>', fs.map);
    var descId = this.object(this.alloc(),
      '<</Type/FontDescriptor/FontName/GlyphLessFont/Flags 5/FontBBox[0 0 ' + GLYPHLESS_ADVANCE + ' 1000]' +
      '/ItalicAngle 0/Ascent 1000/Descent 0/CapHeight 1000/StemV 80/FontFile2 ' + fileId + ' 0 R>>');
    var cidId = this.object(this.alloc(),
      '<</Type/Font/Subtype/CIDFontType2/BaseFont/GlyphLessFont/CIDSystemInfo<</Registry(Adobe)/Ordering(Identity)/Supplement 0>>' +
      '/FontDescriptor ' + descId + ' 0 R/DW ' + GLYPHLESS_ADVANCE + '/CIDToGIDMap ' + mapId + ' 0 R>>');
    var tuId = this.put('<<>>', fs.cmap);
    this.fontId = this.object(this.alloc(),
      '<</Type/Font/Subtype/Type0/BaseFont/GlyphLessFont/Encoding/Identity-H/DescendantFonts[' + cidId + ' 0 R]/ToUnicode ' + tuId + ' 0 R>>');
    return this.fontId;
  };
  function fontStreamsRaw() {
    var ttf = buildGlyphlessFont();
    // CIDToGIDMap: every CID -> glyph 1
    var map = new Uint8Array(65536 * 2);
    for (var i = 0; i < 65536; i++) { map[i * 2] = 0; map[i * 2 + 1] = 1; }
    var cmap = latin1('/CIDInit /ProcSet findresource begin\n12 dict begin\nbegincmap\n' +
      '/CIDSystemInfo <</Registry (Adobe) /Ordering (UCS) /Supplement 0>> def\n/CMapName /Adobe-Identity-UCS def\n/CMapType 2 def\n' +
      '1 begincodespacerange\n<0000> <FFFF>\nendcodespacerange\n1 beginbfrange\n<0000> <FFFF> <0000>\nendbfrange\n' +
      'endcmap\nCMapName currentdict /CMap defineresource pop\nend\nend\n');
    return { ttfLength: ttf.length, ttf: { bytes: ttf, z: false }, map: { bytes: map, z: false }, cmap: { bytes: cmap, z: false } };
  }
  // The font's streams compressed with an async deflate (bytes -> Promise).
  async function fontStreams(deflateAsync) {
    var raw = fontStreamsRaw();
    async function z(st) {
      try {
        var out = await deflateAsync(st.bytes);
        if (out && out.length < st.bytes.length) return { bytes: out, z: true };
      } catch (e) { /* stored */ }
      return st;
    }
    return { ttfLength: raw.ttfLength, ttf: await z(raw.ttf), map: await z(raw.map), cmap: await z(raw.cmap) };
  }
  // An image XObject from pre-encoded bytes.
  // img: {width, height, bytes, filter: 'DCT'|'Flate'|'CCITT'|null,
  //       colorSpace: 'DeviceRGB'|'DeviceGray', bpc, mask: true (ImageMask),
  //       decodeInvert, smaskId/maskId}
  // JBIG2Globals streams by key (one per shared DjVu dictionary).
  PdfWriter.prototype.globals = function (key, bytes) {
    this.globalIds = this.globalIds || {};
    if (!this.globalIds[key]) this.globalIds[key] = this.object(this.alloc(), '<<>>', bytes);
    return this.globalIds[key];
  };
  PdfWriter.prototype.image = function (img) {
    if (img.filter === 'JBIG2' && img.globalsKey != null && !img.globalsId) {
      var gid = this.globalIds && this.globalIds[img.globalsKey];
      if (!gid) throw new Error('JBIG2 globals ' + img.globalsKey + ' were not written');
      img.globalsId = gid;
    }
    var d = '<</Type/XObject/Subtype/Image/Width ' + img.width + '/Height ' + img.height;
    if (img.mask) d += '/ImageMask true' + (img.decodeInvert ? '/Decode[1 0]' : '');
    else d += '/ColorSpace/' + img.colorSpace + '/BitsPerComponent ' + (img.bpc || 8) + (img.decode ? '/Decode' + img.decode : '');
    if (img.maskId) d += '/Mask ' + img.maskId + ' 0 R';
    if (img.filter === 'DCT') d += '/Filter/DCTDecode';
    else if (img.filter === 'Flate') d += '/Filter/FlateDecode';
    else if (img.filter === 'CCITT') d += '/Filter/CCITTFaxDecode/DecodeParms<</K -1/Columns ' + img.width + '/Rows ' + img.height + '>>';
    else if (img.filter === 'JBIG2') d += '/Filter/JBIG2Decode' + (img.globalsId ? '/DecodeParms<</JBIG2Globals ' + img.globalsId + ' 0 R>>' : '');
    d += '>>';
    return this.object(this.alloc(), d, img.bytes);
  };
  // page: {widthPt, heightPt, rotate, contents [{bytes, z}], xobjects
  //        {name: id}, font (bool), annots [fn(writer) -> dict string]}
  PdfWriter.prototype.page = function (page) {
    var contentIds = [];
    for (var ci = 0; ci < page.contents.length; ci++) contentIds.push(this.put('<<>>', page.contents[ci]) + ' 0 R');
    var res = '<<';
    var names = Object.keys(page.xobjects || {});
    if (names.length) {
      res += '/XObject<<';
      for (var i = 0; i < names.length; i++) res += '/' + names[i] + ' ' + page.xobjects[names[i]] + ' 0 R';
      res += '>>';
    }
    if (page.font) res += '/Font<</F1 ' + this.textFont() + ' 0 R>>';
    res += '/ProcSet[/PDF/Text/ImageB/ImageC/ImageI]>>';
    var id = this.pages[page.index];
    if (!id) throw new Error('page ' + page.index + ' was not reserved');
    var d = '<</Type/Page/Parent ' + this.pagesId + ' 0 R/MediaBox[0 0 ' + num(page.widthPt) + ' ' + num(page.heightPt) + ']' +
      '/Resources ' + res + '/Contents ' + (contentIds.length === 1 ? contentIds[0] : '[' + contentIds.join(' ') + ']');
    if (page.rotate) d += '/Rotate ' + page.rotate;
    if (page.annots && page.annots.length) {
      var ids = [];
      for (var a = 0; a < page.annots.length; a++) ids.push(this.object(this.alloc(), page.annots[a](this)) + ' 0 R');
      d += '/Annots[' + ids.join(' ') + ']';
    }
    d += '>>';
    this.object(id, d);
    return id;
  };
  // outline: [{title, page (index) | url, children}]
  PdfWriter.prototype.outline = function (items) {
    if (!items || !items.length) return 0;
    var self = this;
    var rootId = this.alloc();
    function count(nodes) { var n = 0; for (var i = 0; i < nodes.length; i++) n += 1 + count(nodes[i].children || []); return n; }
    function level(nodes, parentId) {
      var ids = [];
      for (var i = 0; i < nodes.length; i++) ids.push(self.alloc());
      for (i = 0; i < nodes.length; i++) {
        var n = nodes[i];
        var kids = n.children || [];
        var d = '<</Title ' + pdfText(n.title) + '/Parent ' + parentId + ' 0 R';
        if (i > 0) d += '/Prev ' + ids[i - 1] + ' 0 R';
        if (i < nodes.length - 1) d += '/Next ' + ids[i + 1] + ' 0 R';
        if (kids.length) {
          var kidIds = level(kids, ids[i]);
          d += '/First ' + kidIds[0] + ' 0 R/Last ' + kidIds[kidIds.length - 1] + ' 0 R/Count ' + count(kids);
        }
        if (typeof n.page === 'number' && self.pages[n.page]) d += '/Dest[' + self.pages[n.page] + ' 0 R/Fit]';
        else if (n.url) d += '/A<</S/URI/URI ' + pdfUri(n.url) + '>>';
        d += '>>';
        self.object(ids[i], d);
      }
      return ids;
    }
    var top = level(items, rootId);
    this.object(rootId, '<</Type/Outlines/First ' + top[0] + ' 0 R/Last ' + top[top.length - 1] + ' 0 R/Count ' + count(items) + '>>');
    return rootId;
  };
  // doc: {info: {Title, Author, ...}, outline, labels: [string|null per page], lang}
  PdfWriter.prototype.finish = function (doc) {
    doc = doc || {};
    var kids = '';
    for (var i = 0; i < this.pages.length; i++) kids += this.pages[i] + ' 0 R ';
    this.object(this.pagesId, '<</Type/Pages/Kids[' + kids + ']/Count ' + this.pages.length + '>>');
    var outlineId = this.outline(doc.outline || []);
    var cat = '<</Type/Catalog/Pages ' + this.pagesId + ' 0 R';
    if (outlineId) cat += '/Outlines ' + outlineId + ' 0 R/PageMode/UseOutlines';
    // Page labels: DjVu page titles where the book has them, the book's own
    // numbers elsewhere (labelStart: the number of the first page written —
    // a range from page 50 keeps reading "50").
    var labels = doc.labels || [];
    var start = doc.labelStart || 1;
    if (labels.some(function (l) { return l; }) || start !== 1) {
      var nums = '';
      var prevCustom = null;
      for (i = 0; i < this.pages.length; i++) {
        var l = labels[i];
        if (l) { nums += i + '<</P ' + pdfText(l) + '>>'; prevCustom = true; }
        else if (prevCustom !== false) { nums += i + '<</S/D/St ' + (start + i) + '>>'; prevCustom = false; }
      }
      cat += '/PageLabels<</Nums[' + nums + ']>>';
    }
    if (doc.lang) cat += '/Lang' + pdfText(doc.lang);
    cat += '>>';
    var catId = this.object(this.alloc(), cat);
    var info = doc.info || {};
    var infoD = '<<';
    var keys = Object.keys(info);
    for (i = 0; i < keys.length; i++) if (info[keys[i]]) infoD += '/' + keys[i] + ' ' + pdfText(info[keys[i]]);
    infoD += '/CreationDate ' + pdfDate(new Date()) + '>>';
    var infoId = this.object(this.alloc(), infoD);
    var xref = this.offset;
    var n = this.nextId;
    var lines = 'xref\n0 ' + n + '\n0000000000 65535 f \n';
    for (i = 1; i < n; i++) {
      var off = this.offsets[i];
      if (off < 0) throw new Error('PDF object ' + i + ' allocated but never written');
      lines += ('0000000000' + off).slice(-10) + ' 00000 n \n';
    }
    var idHex = '';
    for (i = 0; i < 16; i++) idHex += hex2(Math.floor(Math.random() * 256));
    lines += 'trailer\n<</Size ' + n + '/Root ' + catId + ' 0 R/Info ' + infoId + ' 0 R/ID[<' + idHex + '><' + idHex + '>]>>\nstartxref\n' + xref + '\n%%EOF\n';
    this.write(latin1(lines));
  };

  // ------------------------------------------------------------------
  // Invisible text for one page. runs: [{text, xmin, ymin, xmax, ymax}] in
  // DjVu pixels (origin bottom-left); scale = points per pixel.
  // ------------------------------------------------------------------
  // Text layers in the wild are not always tidy: some encoders give every
  // word of a line the LINE's box, others cut a word into one-letter "lines"
  // ("Pag" "e" "1" "of"). A PDF viewer that sees two words drawn on top of
  // each other drops letters from search and copy (PDFium does), so:
  // pieces sitting on one baseline and continuing to the right become one
  // line, and a line whose words overlap is laid out again — the line's box
  // shared out by the length of each word.
  function layoutRuns(runs) {
    var out = [];
    var i, r, lineId = 0, prev = null;
    for (i = 0; i < runs.length; i++) {
      r = runs[i];
      if (!(r.xmax > r.xmin && r.ymax > r.ymin)) continue;
      var joins = false;
      if (prev) {
        if (r.line === prev.line) joins = true;
        else {
          var lo = Math.max(r.ymin, prev.ymin), hi = Math.min(r.ymax, prev.ymax);
          var ov = hi - lo, minH = Math.min(r.ymax - r.ymin, prev.ymax - prev.ymin);
          joins = ov >= 0.6 * minH && r.xmin >= prev.xmin && r.xmin - prev.xmax < 3 * (prev.ymax - prev.ymin);
        }
      }
      if (!joins) lineId++;
      var c = { text: r.text, xmin: r.xmin, ymin: r.ymin, xmax: r.xmax, ymax: r.ymax, line: lineId };
      out.push(c);
      prev = c;
    }
    // per line: words must advance left to right without overlapping
    var start = 0;
    while (start < out.length) {
      var end = start;
      while (end + 1 < out.length && out[end + 1].line === out[start].line) end++;
      var tangled = false;
      for (i = start + 1; i <= end; i++) if (out[i].xmin <= out[i - 1].xmin) { tangled = true; break; }
      if (tangled) {
        var x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity, chars = 0;
        for (i = start; i <= end; i++) {
          x0 = Math.min(x0, out[i].xmin); x1 = Math.max(x1, out[i].xmax);
          y0 = Math.min(y0, out[i].ymin); y1 = Math.max(y1, out[i].ymax);
          chars += out[i].text.length + (i < end ? 1 : 0);
        }
        var unit = (x1 - x0) / Math.max(1, chars), cum = 0;
        for (i = start; i <= end; i++) {
          var len = out[i].text.length;
          out[i].xmin = Math.round(x0 + cum * unit);
          out[i].xmax = Math.max(out[i].xmin + 1, Math.round(x0 + (cum + len) * unit));
          out[i].ymin = y0; out[i].ymax = y1;
          cum += len + 1;
        }
      } else {
        for (i = start; i < end; i++) if (out[i].xmax > out[i + 1].xmin) out[i].xmax = Math.max(out[i].xmin + 1, out[i + 1].xmin);
      }
      start = end + 1;
    }
    return out;
  }

  function textOps(runs, scale) {
    if (!runs || !runs.length) return '';
    runs = layoutRuns(runs);
    // DjVu pixels as the unit (one cm), so every size and position is an
    // integer: a book's text layer compresses about a third smaller.
    var out = 'q ' + num(scale) + ' 0 0 ' + num(scale) + ' 0 0 cm\nBT 3 Tr\n';
    var lastH = -1;
    for (var i = 0; i < runs.length; i++) {
      var r = runs[i];
      var next = runs[i + 1];
      var h = r.ymax - r.ymin;
      var w = r.xmax - r.xmin;
      if (h <= 0 || w <= 0) continue;
      var nChars = 0;
      for (var k = 0; k < r.text.length; k++) {
        var cc = r.text.charCodeAt(k);
        if (cc < 0xdc00 || cc > 0xdfff) nChars++; // a surrogate pair is one visible character
      }
      if (!nChars) continue;
      // Between words of one line the space is part of the word and the
      // stretch makes word + space end exactly where the next word starts
      // (Tesseract's layout). A space that runs into the next word makes
      // PDFium drop letters from search and copy — measured: "Page" -> "Pag".
      var span = w, glyphs = nChars, text = r.text;
      if (next && next.line === r.line && next.xmin > r.xmin) {
        span = next.xmin - r.xmin;
        glyphs = nChars + 1;
        text += ' ';
      }
      var natural = glyphs * h * GLYPHLESS_ADVANCE / 1000;
      var tz = Math.round(1000 * span / natural) / 10;
      var hexs = '';
      for (k = 0; k < text.length; k++) {
        var u = text.charCodeAt(k);
        hexs += hex2(u >> 8) + hex2(u & 0xff);
      }
      if (h !== lastH) { out += '/F1 ' + h + ' Tf '; lastH = h; }
      out += num(tz) + ' Tz 1 0 0 1 ' + r.xmin + ' ' + r.ymin + ' Tm <' + hexs + '> Tj\n';
    }
    return out + 'ET\nQ\n';
  }

  // A packed top-down mask (w x h) grown to (pw x ph). DjVu layers are
  // anchored at the bottom-left, so the extra rows go on TOP and the extra
  // columns on the right.
  function padMask(bits, w, h, stride, pw, ph) {
    if (pw === w && ph === h) return { bits: bits, stride: stride, width: w, height: h };
    var ps = (pw + 7) >> 3;
    var out = new Uint8Array(ps * ph);
    var top = ph - h;
    for (var y = 0; y < h; y++) out.set(bits.subarray(y * stride, y * stride + stride), (top + y) * ps);
    return { bits: out, stride: ps, width: pw, height: ph };
  }

  // The drawing operators of one page, from prepared layers. Names match
  // what assemblePage registers: Bg, Fg (painted through its explicit mask),
  // M0..Mn (stencils, each in its own colour).
  function contentFor(spec) {
    var s = 72 / spec.dpi;
    var c = 'q\n';
    if (spec.bg) {
      c += 'q ' + num(spec.bg.img.width * spec.bg.red * s) + ' 0 0 ' + num(spec.bg.img.height * spec.bg.red * s) + ' 0 0 cm /Bg Do Q\n';
    }
    if (spec.fg && spec.fgMask) {
      c += 'q ' + num(spec.fg.img.width * spec.fg.red * s) + ' 0 0 ' + num(spec.fg.img.height * spec.fg.red * s) + ' 0 0 cm /Fg Do Q\n';
    }
    var masks = spec.masks || [];
    for (var i = 0; i < masks.length; i++) {
      var m = masks[i];
      var col = m.color || [0, 0, 0];
      // x, y: the stencil's bottom-left corner in DjVu px
      c += 'q ' + num(col[0] / 255) + ' ' + num(col[1] / 255) + ' ' + num(col[2] / 255) + ' rg ' +
        num(m.img.width * s) + ' 0 0 ' + num(m.img.height * s) + ' ' + num((m.x || 0) * s) + ' ' + num((m.y || 0) * s) + ' cm /M' + i + ' Do Q\n';
    }
    c += 'Q\n';
    return c + textOps(spec.runs, s);
  }

  // One PDF page from prepared layers (all image bytes already encoded).
  // spec: {index, width, height (DjVu px), dpi, rotation,
  //        bg:   {img, red}        background image covering red*img px,
  //        fg:   {img, red}        colour image painted through fgMask,
  //        fgMask: {img}           explicit mask padded to the fg coverage,
  //        masks: [{img, color, x, y}]  stencils painted with a solid colour,
  //        runs  (text, DjVu px),  links [{box, url | page}],
  //        content: {bytes, z}     contentFor(spec), compressed elsewhere
  //                                (built here when absent),
  //        extra: [{bytes, z}]     more content streams (OCR text),
  //        hasText                 some stream draws text (font needed)}
  function assemblePage(w, spec) {
    var s = 72 / spec.dpi;
    var xo = {};
    // shared JBIG2 dictionaries arrive with the first page that needs them
    var gl = spec.globals || [];
    for (var gi = 0; gi < gl.length; gi++) w.globals(gl[gi].key, gl[gi].bytes);
    if (spec.bg) xo.Bg = w.image(spec.bg.img);
    if (spec.fg && spec.fgMask) {
      var maskId = w.image(spec.fgMask.img);
      var fgImg = {};
      for (var k in spec.fg.img) if (Object.prototype.hasOwnProperty.call(spec.fg.img, k)) fgImg[k] = spec.fg.img[k];
      fgImg.maskId = maskId;
      xo.Fg = w.image(fgImg);
    }
    var masks = spec.masks || [];
    for (var i = 0; i < masks.length; i++) xo['M' + i] = w.image(masks[i].img);
    var contents = [spec.content || { bytes: latin1(contentFor(spec)), z: false }];
    var extra = spec.extra || [];
    for (var e = 0; e < extra.length; e++) contents.push(extra[e]);
    var hasText = !!spec.hasText || !!(spec.runs && spec.runs.length);
    var annots = [];
    var links = spec.links || [];
    for (var l = 0; l < links.length; l++) {
      (function (lk) {
        var b = lk.box;
        var rect = '[' + num(b.xmin * s) + ' ' + num(b.ymin * s) + ' ' + num(b.xmax * s) + ' ' + num(b.ymax * s) + ']';
        if (typeof lk.page === 'number') {
          annots.push(function (wr) {
            var target = wr.pages[lk.page];
            return '<</Type/Annot/Subtype/Link/Rect' + rect + '/Border[0 0 0]/Dest[' + target + ' 0 R/Fit]>>';
          });
        } else if (lk.url) {
          annots.push(function () {
            return '<</Type/Annot/Subtype/Link/Rect' + rect + '/Border[0 0 0]/A<</S/URI/URI ' + pdfUri(lk.url) + '>>>>';
          });
        }
      })(links[l]);
    }
    return w.page({
      index: spec.index,
      widthPt: spec.width * s,
      heightPt: spec.height * s,
      rotate: spec.rotation || 0,
      contents: contents,
      xobjects: xo,
      font: hasText,
      annots: annots
    });
  }

  return {
    PdfWriter: PdfWriter,
    assemblePage: assemblePage,
    contentFor: contentFor,
    fontStreams: fontStreams,
    padMask: padMask,
    encodeG4: encodeG4,
    buildGlyphlessFont: buildGlyphlessFont,
    textOps: textOps,
    layoutRuns: layoutRuns,
    pdfText: pdfText,
    pdfUri: pdfUri,
    num: num,
    latin1: latin1
  };
});
